// context/AppContext.js
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { getUserProfile, UpdateTour } from '../helper/backend-methods';
import { showToast } from '../components/Toast';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [isTrial, setIsTrial] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [isActiveSubscription, setIsActiveSubscription] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const hasFetchedRef = useRef(false);

  // Get started start - YOUR EXISTING CODE
  const defaultTasks = [
    { id: 1, title: "Create a Prompt", description: "Define the instructions your agent will follow. A clear prompt sets the foundation for accurate and effective responses.", completed: false, apiKey: "prompt" },
    { id: 2, title: "Create an Agent", description: "Build your AI agent by selecting the right tools, configurations, and workflows to bring your prompt to life.", completed: false, apiKey: "agent" },
    { id: 3, title: "Make a Call", description: "Call your own phone and hear your agent handle a real conversation.", completed: false, apiKey: "call" },
    { id: 4, title: "Choose a Plan", description: "Pick the plan that keeps your agents live and answering calls after your trial.", completed: false, apiKey: "plan" },
  ];
  const [tasks, setTasks] = useState(defaultTasks);

  // Quick-start pop-up: shown once to new users, and on demand from Getting Started.
  const [quickStartEligible, setQuickStartEligible] = useState(false);
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  const [quickStartSkipWelcome, setQuickStartSkipWelcome] = useState(false);
  // Goes up each time the quick-start creates an agent, so lists can refresh.
  const [quickStartAgentsCreated, setQuickStartAgentsCreated] = useState(0);
  const [activeTask, setActiveTask] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [userId, setUserId] = useState(null);
  const [twilioPhone, setTwilioPhone] = useState(null);

  // NEW: Tour state - ADDED FOR MULTI-PAGE TOUR
  const [activeTour, setActiveTour] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completedTask, setCompletedTask] = useState(null);

  const resetOnboarding = () => {
    setTasks(defaultTasks);
    setActiveTask(null);
    setActiveTour(null);
    setShowCompletionModal(false);
    setCompletedTask(null);
    setTwilioPhone(null)
    localStorage.removeItem("onboarding");
    hasFetchedRef.current = false;
  };

  // useEffect(() => {
  //   const saved = localStorage.getItem("onboarding");
  //   if (saved) {
  //     const data = JSON.parse(saved);
  //     setTasks(data.tasks || defaultTasks);
  //     setActiveTask(data.activeTask || null);

  //     // Also load active tour from localStorage
  //     if (data.activeTour) {
  //       setActiveTour(data.activeTour);
  //     }
  //   }
  // }, []);

  const fetchUserOnboardingStatus = async (userId) => {
    try {
      const data = await getUserProfile({ userId: userId });
      if(!data.data?.isVerified){
        window.location.href = '/logout';
      }
      if(data.data?.twilioPhone){
        setTwilioPhone(data.data.twilioPhone);
      }
      if (data.data.getting_started) {
        const gettingStarted = data.data.getting_started;
        const updatedTasks = tasks.map(task => ({
          ...task,
          completed: gettingStarted.isCompleted ? true : gettingStarted[task.apiKey]
        }));
        setIsCompleted(gettingStarted.isCompleted)
        setTasks(updatedTasks);
        setQuickStartEligible(
          !gettingStarted.quickStartSeen &&
          !gettingStarted.isCompleted &&
          !gettingStarted.prompt &&
          !gettingStarted.agent &&
          !gettingStarted.call
        );
      } else {
        showToast("error", "Request Failed", data.message)
      }
    } catch (err) {
      showToast("error", "Request Failed", err.message)
    }
  };
  
  useEffect(() => {
    const currentUserId = localStorage.getItem("userId");
    if (currentUserId) {
      setUserId(currentUserId);
      fetchUserOnboardingStatus(currentUserId)
      hasFetchedRef.current = true;
      // getUserProfile({ userId: currentUserId }).then((data) => {
      //   if (data.data.getting_started) {
      //     const updatedTasks = tasks.map(task => ({
      //       ...task,
      //       completed: data.data.getting_started.isCompleted ? true : data.data.getting_started[task.apiKey]
      //     }));
      //     setIsCompleted(data.data.getting_started.isCompleted)
      //     setTasks(updatedTasks);
      //   }
      //   else {
      //     showToast("error", "Request Failed", data.message)
      //   }
      // }).catch((err) => {
      //   showToast("error", "Request Failed", err.message)
      // })
    }
  }, [userId]);
  useEffect(() => {
    const saved = localStorage.getItem("onboarding");
    if (saved) {
      try {
        const data = JSON.parse(saved);
        // Keep only the saved progress; titles and descriptions come from the
        // current defaults so copy fixes reach people who visited before.
        setTasks(defaultTasks.map(task => ({
          ...task,
          completed: Boolean(data.tasks?.find(savedTask => savedTask.id === task.id)?.completed)
        })));
        setActiveTask(data.activeTask || null);

        // Also load active tour from localStorage
        if (data.activeTour) {
          setActiveTour(data.activeTour);
        }
      } catch (error) {
        console.error('Error parsing onboarding data:', error);
        resetOnboarding(); // Reset if data is corrupted
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("onboarding", JSON.stringify({
      tasks,
      activeTask,
      activeTour // Save active tour state
    }));
  }, [tasks, activeTask, activeTour]);


  const updateTaskCompletionAPI = async (taskId, completed) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task || !userId) return;

      const formData = {
        getting_started: {
          isCompleted: tasks.every(t => t.id === taskId ? completed : t.completed),
          prompt: taskId === 1 ? completed : tasks.find(t => t.id === 1)?.completed || false,
          agent: taskId === 2 ? completed : tasks.find(t => t.id === 2)?.completed || false,
          call: taskId === 3 ? completed : tasks.find(t => t.id === 3)?.completed || false,
          plan: taskId === 4 ? completed : tasks.find(t => t.id === 4)?.completed || false,
        },
        userId: userId
      }
      UpdateTour(formData).then((data) => {
        if ((data.status === 201 || data.status === 200)) {
  
        } else {
        }
        setIsCompleted(tasks.every(t => t.id === taskId ? completed : t.completed))
      }).catch((e) => {
        console.log("Tour Request Failed", e)
      })
      
      console.log('✅ Task completion updated to API');
    } catch (error) {
      console.error('❌ Failed to update task completion:', error);
    }
  };

  // NEW: Tour-specific function - ADDED FOR MULTI-PAGE TOUR
  const markTaskDoneTour = async (taskId, showCompletionModal = true) => {
    const task = tasks.find(t => t.id === taskId);

    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, completed: true } : t));
    setActiveTour(null);

    await updateTaskCompletionAPI(taskId, true);

    if (task && showCompletionModal) {
        setCompletedTask(task);
        setShowCompletionModal(true);
    }
};

  // NEW: Start tour function - ADDED FOR MULTI-PAGE TOUR
  const startTaskTour = (taskId) => {
    console.log(`Starting tour for task ${taskId}`);
    setActiveTour({ taskId, stepIndex: 0 });
  };

  // NEW: Handle completion modal close
  const handleCompletionModalClose = () => {
    setShowCompletionModal(false);
    setCompletedTask(null);
  };

  const progress = (tasks.filter(t => t.completed).length / tasks.length) * 100;

  const openQuickStart = ({ skipWelcome = false } = {}) => {
    setQuickStartSkipWelcome(skipWelcome);
    setQuickStartOpen(true);
  };

  const closeQuickStart = () => setQuickStartOpen(false);

  // Read userId from storage: the userId state is only set on a full page load.
  const markQuickStartSeen = () => {
    setQuickStartEligible(false);
    UpdateTour({ getting_started: { quickStartSeen: true }, userId: localStorage.getItem("userId") })
      .catch((e) => console.log("Tour Request Failed", e));
  };

  // The quick-start creates a prompt and an agent, so both checklist steps tick together.
  const markQuickStartDone = () => {
    setQuickStartAgentsCreated(count => count + 1);
    setTasks(prev => prev.map(t => (t.apiKey === "prompt" || t.apiKey === "agent") ? { ...t, completed: true } : t));
    UpdateTour({ getting_started: { prompt: true, agent: true }, userId: localStorage.getItem("userId") })
      .catch((e) => console.log("Tour Request Failed", e));
  };

  // YOUR EXISTING FUNCTION
  const updateTrialInfo = (trialData) => {
    setIsTrial(trialData.isTrial);
    setDaysRemaining(trialData.daysRemaining);
    setTrialEndDate(trialData.trialEndDate);
  };

  // Value that will be available to all components
  const value = {
    // YOUR EXISTING VALUES
    isTrial,
    setIsTrial,
    daysRemaining,
    setDaysRemaining,
    trialEndDate,
    setTrialEndDate,
    updateTrialInfo,
    isActiveSubscription,
    setIsActiveSubscription,
    tasks,
    setTasks,
    activeTask,
    setActiveTask,
    progress,

    // NEW: Tour values - ADDED FOR MULTI-PAGE TOUR
    activeTour,
    setActiveTour,
    markTaskDoneTour,
    startTaskTour,
    showCompletionModal,
    setShowCompletionModal,
    completedTask,
    handleCompletionModalClose,
    resetOnboarding,
    isCompleted,
    fetchUserOnboardingStatus,
    twilioPhone,

    // Quick-start pop-up
    quickStartEligible,
    quickStartOpen,
    quickStartSkipWelcome,
    quickStartAgentsCreated,
    openQuickStart,
    closeQuickStart,
    markQuickStartSeen,
    markQuickStartDone
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;